/**
 * Brand context — fetches the platform (or partner-specific) brand
 * config once on mount so every un-authenticated page can render the
 * right logo / colours / copy without hard-coding "Aii Group".
 *
 * Two entry points:
 *   • <BrandProvider>   — reads platform config (used by <Login> at `/login`)
 *   • <PartnerBrandProvider slug={slug}>  — reads /api/brand/partner/:slug
 *     and inherits missing fields from the platform config
 *     (used by `/p/:slug` landing URLs)
 *
 * A sensible in-memory default is exposed immediately so pages render
 * before the network call finishes — otherwise the login screen would
 * flash blank on cold loads.
 */
import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const FALLBACK = {
  name: "Aii Group",
  wordmark: "AII GROUP",
  tagline: "Meaningful Adventures",
  hero_line: "Sign in to your world",
  hero_accent: "world",
  hero_subtitle: "One command centre for the team, one welcoming portal for participants, and a dedicated door for our sending partners. Choose your door.",
  logo_url: "/aii-logo.png",
  favicon_url: "/favicon.ico",
  primary_color: "#1B7A3E",
  primary_dark: "#0b3d1f",
  footer_text: "Unauthorised access is prohibited.",
  hide_vendor_wordmark: false,
  team_card_title: "Team sign in",
  team_card_kicker: "For Team",
  team_card_blurb: "Founder · BDOM · PCWM · GEHM · SMCM — access your operations command centre.",
  participant_card_kicker: "For Participants",
  participant_card_blurb: "Welcome, traveller! Complete your C-form, book activities, request services and share feedback — all in one place.",
  participant_card_footnote: "Don't have credentials yet? Your coordinator will send them by email & WhatsApp.",
  partner_card_kicker: "For Sending Partners",
  partner_card_blurb: "Universities · Sending organisations · Representatives — submit participant applications, book weekend tours, and grab pre-arrival materials.",
  partner_card_footnote: "Not a partner yet? Reach out to your platform contact — Founder / BDOM will create your credentials.",
};

const BrandContext = createContext(FALLBACK);

function useApplyBrand(brand) {
  // Reflect the brand into <title> and the CSS variable used by
  // ambient tailwind utility `bg-brand`. Only touches document.title
  // when the brand actually has a value — refuses to blank it.
  useEffect(() => {
    if (brand?.name) {
      document.title = `${brand.name}${brand.tagline ? " · " + brand.tagline : ""}`;
    }
    if (brand?.favicon_url) {
      let el = document.querySelector('link[rel="icon"]');
      if (!el) {
        el = document.createElement("link");
        el.rel = "icon";
        document.head.appendChild(el);
      }
      el.href = brand.favicon_url;
    }
  }, [brand?.name, brand?.tagline, brand?.favicon_url]);
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(FALLBACK);
  useEffect(() => {
    let alive = true;
    api.get("/brand/config")
      .then(({ data }) => { if (alive) setBrand({ ...FALLBACK, ...data }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  useApplyBrand(brand);
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function PartnerBrandProvider({ slug, children }) {
  const [brand, setBrand] = useState(FALLBACK);
  useEffect(() => {
    let alive = true;
    if (!slug) return;
    api.get(`/brand/partner/${encodeURIComponent(slug)}`)
      .then(({ data }) => { if (alive) setBrand({ ...FALLBACK, ...data }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);
  useApplyBrand(brand);
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext);
}
